// SF-174 Component 6: `estimatedGain.ts` — per-Attack-Type client-side gain estimators, computed
// entirely from already-fetched intel (no new backend action; see Complexity Tier/Component 6 for
// why a `PreviewAttackGainAction` was rejected). Mirrors `MilitarySystem`/`CombatSystem`'s real
// formulas (docs/GAME_MECHANICS.md), the same accepted client-side-duplication pattern
// `minimumWinningForce.ts` already uses for the offense side.
//
// `gainMod`'s Province Networth Factor (PNF), military science, own+target kingdom stance, and
// target dragon effect are all computed exactly. The remaining 4 factors (guard stations, race,
// kingdom relations, Kingdom Networth Factor) stay flat 1.0 — a deliberate, narrowed approximation
// (see Component 6/Configuration for the full justification); this file's numbers are therefore
// framed as estimates by its caller (`EstimatedGainPanel`), not exact predictions.

import type { CrystalBallData, SpyOnScienceData, SpyOnThroneData } from '../types/intel';

const STANCE_ATTACK_GAINS_MOD: Record<string, number> = {
  NORMAL: 1.0,
  AGGRESSIVE: 1.10,
  FORTIFIED: 0.50,
};

/** `InMemoryPropertySystem.java:397,408` — an unrecognized stance string falls back to 1.0. */
export function stanceAttackGainsMod(stance: string): number {
  return STANCE_ATTACK_GAINS_MOD[stance] ?? 1.0;
}

/**
 * `MilitarySystem.java:539-554`'s piecewise Province Networth Factor. Mirrors the Java's cascading
 * strict-inequality if/else-if exactly, including its real discontinuity: at each exact boundary
 * value (rpnw === 0.567 / 0.9 / 1.1 / 1.6) BOTH neighboring branches are false (both sides use
 * strict </>), so it falls through to the final `else 0`, not to whichever branch the value looks
 * like it "belongs to."
 */
export function computePNF(rpnw: number): number {
  if (rpnw < 0.567) return 0;
  if (rpnw > 0.567 && rpnw < 0.9) return 3 * rpnw - 1.7;
  if (rpnw > 0.9 && rpnw < 1.1) return 1.0;
  if (rpnw > 1.1 && rpnw < 1.6) return -2 * rpnw + 3.2;
  return 0;
}

export interface GainModContext {
  targetNetworth: number;
  ownNetworth: number;
  /** `province.sciences.progress['MILITARY']?.effectPercentage ?? 0` */
  militaryEffectPercentage: number;
  /** `province.kingdom.isAtWar` — war-negates the OWN side's stance mod only. */
  isOwnKingdomAtWar: boolean;
  /** Own kingdom's stance, already fetched at page-mount. */
  ownStance: string;
  /** Target kingdom's stance, fetched via the "Load Kingdom" flow. Never war-negated. */
  targetStance: string;
  /** Target kingdom's dragon type, or null. Only EMERALD has a registered ATTACK_GAINS_MOD. */
  targetDragonType: string | null;
}

/** Non-Massacre `gainMod` chain: `pnf * scienceMod * stanceMod * targetStanceMod * dragonMod`. */
export function computeGainMod(ctx: GainModContext): number {
  const rpnw = ctx.ownNetworth > 0 ? ctx.targetNetworth / ctx.ownNetworth : 0;
  const pnf = computePNF(rpnw);
  const scienceMod = 1 + ctx.militaryEffectPercentage;
  const stanceMod = ctx.isOwnKingdomAtWar ? 1.0 : stanceAttackGainsMod(ctx.ownStance);
  const targetStanceMod = stanceAttackGainsMod(ctx.targetStance);
  const dragonMod = ctx.targetDragonType === 'EMERALD' ? 0.85 : 1.0;
  return pnf * scienceMod * stanceMod * targetStanceMod * dragonMod;
}

/**
 * Massacre's separate `massacreMod` chain (`MilitarySystem.java:409-415`): no PNF/KNF term at
 * all, and — unlike the non-Massacre chain — stance is NEVER war-negated for Massacre.
 */
export function computeMassacreMod(ctx: Pick<GainModContext, 'ownStance' | 'targetDragonType'>): number {
  const stanceMod = stanceAttackGainsMod(ctx.ownStance);
  const dragonMod = ctx.targetDragonType === 'EMERALD' ? 0.85 : 1.0;
  return stanceMod * dragonMod;
}

export interface AcresCapturedGain {
  acresCaptured: number;
}

/** Mirrors `MilitarySystem.computeTraditionalMarchAttackGain` + `CombatSystem.calculateLandCapture`. */
export function computeTraditionalMarchGain(
  gainMod: number,
  targetLand: number,
  ownAcres: number
): AcresCapturedGain {
  const baseGainMod = 0.12; // GAIN
  const maxGainMod = 0.20; // MAX_GAIN — always the non-war cap (see file-level note in the spec)
  const resultingMod = Math.min(baseGainMod * gainMod, maxGainMod);
  const baseCapture = targetLand * resultingMod;
  const stageTwoCap = Math.min(ownAcres, targetLand) * 0.20;
  const acresCaptured = Math.floor(Math.max(0, Math.min(baseCapture, stageTwoCap)));
  return { acresCaptured };
}

/**
 * Mirrors `MilitarySystem.computeConquestAttackGain` (`MilitarySystem.java:390-403`). Conquest's
 * land-gain fraction never multiplies by `gainMod` — only by the SOHD-derived offense/defense
 * ratio. `targetTD <= 0` falls back to `10.0` (matches the engine's own convention, avoids NaN).
 */
export function computeConquestGain(
  attackOP: number,
  targetTD: number,
  targetLand: number,
  ownAcres: number
): AcresCapturedGain {
  const offDefRatio = targetTD > 0 ? attackOP / targetTD : 10.0;
  const clampedRatio = Math.min(offDefRatio, 1.0);
  const ratioPct = (clampedRatio - 0.51) / (1.0 - 0.51); // CONQUEST_MIN_OFFENSE
  const scale = 0.40 + ratioPct * (1.0 - 0.40); // CONQUEST_PARTIAL_HIT_MIN
  const conquestMod = 0.068 * scale; // CONQUEST_LAND_GAIN
  const baseCapture = targetLand * conquestMod;
  const stageTwoCap = Math.min(ownAcres, targetLand) * 0.20;
  const acresCaptured = Math.floor(Math.max(0, Math.min(baseCapture, stageTwoCap)));
  return { acresCaptured };
}

export interface PlunderGain {
  gold: number;
  food: number;
  runes: number;
}

/** Mirrors `MilitarySystem.computePlunderAttackGain` (`MilitarySystem.java:347-367`). */
export function computePlunderGain(gainMod: number, cbData: CrystalBallData | null): PlunderGain | null {
  if (!cbData) return null;
  const cap = Math.min(gainMod, 1.35); // GAIN_MOD_CAP
  const gold = Math.floor(cbData.money * 0.50 * cap); // GAIN_GOLD
  const food = Math.floor(cbData.food * 0.60 * cap); // GAIN_FOOD
  const runes = Math.floor(cbData.runes * 0.60 * cap); // GAIN_RUNES
  return { gold, food, runes };
}

export interface RazeGain {
  acresRazed: number;
}

/**
 * Mirrors `MilitarySystem.computeRazeAttackGain` (`MilitarySystem.java:433-450`). Sourced from the
 * public roster's `targetLand`, not Survey — Raze destroys ACRES of land (barren first, then
 * built structures), not a building-type count.
 */
export function computeRazeGain(gainMod: number, targetLand: number, ownAcres: number): RazeGain {
  const baseRazed = targetLand * 0.05 * gainMod; // RAZE_GAIN
  const cap = Math.min(ownAcres, targetLand) * 0.20;
  const acresRazed = Math.floor(Math.min(baseRazed, cap));
  return { acresRazed };
}

export interface LearnCategoryGain {
  scienceType: string;
  stolen: number;
}

export interface LearnGain {
  /** Non-zero categories only, sorted descending by `stolen`. */
  categories: LearnCategoryGain[];
  /** Sum of ALL non-zero categories, not just the (caller-truncated) visible subset. */
  total: number;
}

/** Mirrors `MilitarySystem.computeLearnAttackGain` (`MilitarySystem.java:369-388`), per ScienceType. */
export function computeLearnGain(gainMod: number, sosData: SpyOnScienceData | null): LearnGain | null {
  if (!sosData) return null;
  const categories: LearnCategoryGain[] = [];
  let total = 0;
  for (const category of sosData.sciences) {
    const current = category.booksKnown;
    const floor = current * 0.60; // LEARN_DEFENDER_BOOK_FLOOR
    const computed = Math.floor(current * 0.09375 * gainMod); // LEARN_GAIN_PER_CATEGORY
    const stolen = Math.max(0, Math.min(computed, current - floor));
    if (stolen > 0) {
      categories.push({ scienceType: category.scienceType, stolen });
      total += stolen;
    }
  }
  categories.sort((a, b) => b.stolen - a.stolen);
  return { categories, total };
}

export interface MassacreGain {
  peasantsKilled: number;
  thievesKilled: number;
  wizardsKilled: number;
}

/**
 * Mirrors `MilitarySystem.computeMassacreAttackGain` (`MilitarySystem.java:405-431`) — Peasants/
 * Thieves/Wizards only, no Soldiers. Thieves/Wizards both sourced from `sotData` (Spy on Throne),
 * not `somData` — `SpyOnThroneData` already carries both.
 */
export function computeMassacreGain(
  massacreMod: number,
  cbData: CrystalBallData | null,
  sotData: SpyOnThroneData | null
): MassacreGain | null {
  if (!cbData || !sotData) return null;
  const peasantsKilled = Math.floor(cbData.peasants * 0.095 * massacreMod); // MASSACRE_GAIN_PEASANTS
  const thievesKilled = Math.floor(sotData.thieves * 0.085 * massacreMod); // MASSACRE_GAIN_THIEVES
  const wizardsKilled = Math.floor(sotData.wizards * 0.050 * massacreMod); // MASSACRE_GAIN_WIZARDS
  return { peasantsKilled, thievesKilled, wizardsKilled };
}
